
import React, { useEffect, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { FaEye, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import "../styles/merchants.css";
import { deleteMerchantByID, getAllMerchants } from "../api/api";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../utils/authStorage";

const Merchants = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const toast = React.useRef(null);

  const user = getUserData()?.user; // Fetch stored user info
  const userType = user?.user_type;
  
  const userRoles = Array.isArray(user.roles)
    ? user.roles.flat() // handles nested arrays like [['view-only']]
    : [];

  const isAdmin = userType === "admin";
  const isViewOnly = userRoles.includes("view-only");
  const isMerchantOnboarding = userRoles.includes("merchant_onboarding");
  
  // Fetch merchants
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllMerchants();
        if (res?.data) setMerchants(res?.data);
      } catch (err) {
        console.error("Error fetching merchants:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const refreshMerchants = async () => {
    try {
      const res = await getAllMerchants();
      if (res?.data) setMerchants(res?.data);
    } catch (err) {
      console.error("Error fetching merchants:", err);
    }
  };

  const handleDeleteMerchant = async (mid) => {
    try {
      const res = await deleteMerchantByID(mid);
      if (res?.isSuccess) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Merchant deleted successfully",
          life: 2000,
        });
        refreshMerchants();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res?.message || "Failed to delete Merchant",
          life: 3000,
        });
      }
    } catch (err) {
      console.error("Error deleting Merchant:", err);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while deleting Merchant",
        life: 3000,
      });
    }
  };

  const columns = useMemo(
    () => [
      
      { field: "MID", header: "MID", style: { width: "220px" } },
      {
        field: "MerchantName",
        header: "Merchant Name",
        style: { width: "220px" },
      },
      { field: "Email", header: "Email", style: { width: "220px" } },
      { field: "PhoneNumber", header: "Phone Number", style: { width: "220px" } },
    ],
    []
  );

  const terminalsBodyTemplate = (rowData) => {
    const terminals = rowData.terminals || [];
    const terminalCount = terminals.length;

    if (terminalCount === 0) return <span>0</span>;

    const tooltipContent = `
Terminals:
${terminals.map((t) => `• ${t.TID}`).join("\n")}
    `;

    return (
      <div className="terminals-cell">
        <span className="terminal-count">{terminalCount}</span>
        <FaEye data-pr-tooltip={tooltipContent} data-pr-position="top" className="view-icon" />
        <Tooltip target=".view-icon" />
      </div>
    );
  };

  // ✅ Conditional rendering of Action Icons
  const actionBodyTemplate = (rowData) => (
    <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
      {/* Everyone can view */}
      <FaEye
        className="view-icon"
        title="View"
        onClick={() =>
          navigate("/dashboard/merchant?mode=view", {
            state: { merchant: rowData, mode: "view" },
          })
        }
      />

      {/* Only Admin or Merchant Onboarding can Edit/Delete */}
      {(isAdmin || isMerchantOnboarding) && !isViewOnly && (
        <>
          <FaEdit
            title="Edit"
            className="view-icon"
            onClick={() =>
              navigate("/dashboard/merchant?mode=edit", {
                state: { merchant: rowData, mode: "edit" },
              })
            }
          />
          <FaTrash
            className="delete-icon"
            title="Delete"
            onClick={() => {
              const confirmed = window.confirm(
                `Are you sure you want to delete merchant "${rowData.MID}"?`
              );
              if (confirmed) handleDeleteMerchant(rowData.MID);
            }}
          />
        </>
      )}
    </div>
  );

  // if (loading) {
  //   return (
  //     <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
  //       <p className="loader">Loading Merchants data...</p>
  //     </div>
  //   );
  // }
  if (loading) {
    return (
      <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="db-loader"><div className="loader-spinner" /><p>Loading Merchants data…</p></div>
      </div>
    );
  }

  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
      <Toast ref={toast} />

      {/* ✅ Add Button only for Admin or Merchant Onboarding (not view-only) */}
      {(isAdmin || isMerchantOnboarding) && !isViewOnly && (
        <div className="add-merchant-container">
          <button
            className="add-merchant-btn"
            onClick={() => navigate("/dashboard/merchant?mode=create")}
          >
            <FaPlus />
          </button>
        </div>
      )}

      <section className="reporting-table-section">
        <DataTable
          value={merchants}
          dataKey="msID"
          className="dashboard-table"
          responsiveLayout="scroll"
          paginator
          rows={10}
          paginatorClassName="custom-paginator"
        >
          {columns.map((col) => (
            <Column key={col.field} field={col.field} header={col.header} style={col.style} />
          ))}
          <Column header="No Of Terminals" body={terminalsBodyTemplate} style={{ width: "180px" }} />
          <Column header="Actions" body={actionBodyTemplate} style={{ width: "180px" }} />
        </DataTable>
      </section>
    </div>
  );
};

export default Merchants;
